// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import QtyField from "@awc/App/Components/Qty/QtyField";
import { ElementWrapper, SectionContainer, SectionTitle } from "@awc/App/Widgets/DesignSystem/DesignSystem.helpers";
import { dynamicDropdownOptions } from "@awc/App/Widgets/DesignSystem/DummyData/DummyData";
import Badge, { BadgeTypeValues } from "@awc/MobiusOverrides/Badge/Badge";
import { ButtonWrapper } from "@awc/MobiusOverrides/Button/Button";
import TextField from "@awc/MobiusOverrides/TextField/TextField";
import {
    color,
    getInputStyleWithIcon,
    getSystemColor,
    mergeWithCss,
    sharedBadgeStyle,
    xScreen,
    xValidateOnSubmit,
} from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import DatePicker from "@insite/mobius/DatePicker";
import DynamicDropdown from "@insite/mobius/DynamicDropdown";
import { FormFieldStyle } from "@insite/mobius/FormField";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select/Select";
import TextArea from "@insite/mobius/TextArea";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import styled, { css } from "styled-components";

interface FormTabStyles {
    gridItem?: GridItemProps;
    smallGridItem?: GridItemProps;
    fullGridItem?: GridItemProps;
    formWrapper?: InjectableCss;
    columnFormWrapper?: InjectableCss;
}

const formTabStyles: FormTabStyles = {
    gridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            padding-bottom: 40px;
        `,
    },
    smallGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-wrap: wrap;
            padding-bottom: 40px;
            height: 100%;
        `,
    },
    fullGridItem: {
        width: 12,
    },
    formWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            flex-wrap: wrap;
            ${FormFieldStyle} {
                max-width: 250px;
            }
        `,
    },
    columnFormWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            gap: 16px;
            ${FormFieldStyle} {
                max-width: 250px;
            }
        `,
    },
};

const styles = formTabStyles;

const FormTab: FC = () => {
    const [radioValue, setRadioValue] = useState("All");
    const [qty, setQty] = useState(1);
    const onRadioChangeHandler = (e: any) => {
        setRadioValue(e.target.value);
    };

    return (
        <>
            <SectionContainer
                extendedStyles={{
                    gridContainer: {
                        gap: 0,
                    },
                }}
            >
                {/* <XNG-Changes: Input -------------------------------------------------- */}
                <GridItem {...styles.fullGridItem}>
                    <SectionTitle>Input : Form : Title + Input / Error</SectionTitle>
                </GridItem>
                <GridItem {...styles.smallGridItem}>
                    <StyledWrapper {...styles.columnFormWrapper}>
                        <ElementWrapper title="Inactive">
                            <TextField
                                label="Form Title"
                                data-test-selector="inactive"
                                placeholder="Placeholder"
                                type="userName"
                            />
                        </ElementWrapper>
                        <ElementWrapper title="Disabled">
                            <TextField
                                label="Form Title"
                                data-test-selector="disabled"
                                placeholder="Placeholder"
                                disabled
                            />
                        </ElementWrapper>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.smallGridItem}>
                    <StyledWrapper {...styles.columnFormWrapper}>
                        <ElementWrapper title="Input Text">
                            <TextField
                                label="Form Title"
                                data-test-selector="inputText"
                                placeholder="Placeholder"
                                value="Input Text"
                            />
                        </ElementWrapper>
                        <ElementWrapper title="Error">
                            <TextField
                                label="Form Title"
                                data-test-selector="inputTextError"
                                placeholder="Placeholder"
                                value="This Is Input Text"
                                error="Error Notice Text Goes Here"
                            />
                        </ElementWrapper>
                    </StyledWrapper>
                </GridItem>
            </SectionContainer>
            {/* <XNG-Changes: Input -------------------------------------------------- */}
            <SectionContainer>
                {/* <XNG-Changes: Input No Title ---------------------------------------- */}
                <GridItem {...styles.smallGridItem}>
                    <SectionTitle>Input : Form : No Title</SectionTitle>
                    <StyledWrapper {...styles.columnFormWrapper}>
                        <ElementWrapper title="Inactive">
                            <TextField
                                required
                                data-test-selector="userName"
                                placeholder="Placeholder"
                                type="userName"
                            />
                        </ElementWrapper>
                        <ElementWrapper title="Disabled">
                            <TextField required data-test-selector="disabled" placeholder="Placeholder" disabled />
                        </ElementWrapper>
                    </StyledWrapper>
                </GridItem>
                {/* <XNG-Changes: Input No Title ---------------------------------------- */}

                {/* <XNG-Changes: Search ------------------------------------------------ */}
                <GridItem {...styles.smallGridItem}>
                    <SectionTitle>Input : Search : Header</SectionTitle>
                    <StyledWrapper {...styles.columnFormWrapper}>
                        <ElementWrapper title="Inactive">
                            <TextField
                                data-test-selector="search_inactive"
                                iconClickableProps={{ onClick: () => {} }}
                                {...getInputStyleWithIcon()}
                            />
                        </ElementWrapper>
                        <ElementWrapper title="Disabled">
                            <TextField
                                data-test-selector="search_disabled"
                                iconClickableProps={{ onClick: () => {} }}
                                {...getInputStyleWithIcon()}
                                disabled
                            />
                        </ElementWrapper>
                    </StyledWrapper>
                </GridItem>
                {/* <XNG-Changes: Search ------------------------------------------------ */}
                <GridItem {...styles.smallGridItem}>
                    <SectionTitle>Input : Form : Subsribe</SectionTitle>
                    <StyledWrapper {...styles.columnFormWrapper}>
                        <ElementWrapper title="Inactive">
                            {createWidgetElement("Basic/Subscribe", {
                                fields: {
                                    placeholder: "Enter email address",
                                    label: "Subscribe",
                                    customCSS: css`
                                        ${FormFieldStyle} {
                                            width: 317px;
                                            max-width: unset;
                                            ${xScreen("lte", "sm")} {
                                                width: 100%;
                                            }
                                        }
                                        ${ButtonWrapper}.email-button {
                                            min-height: 40px;
                                            top: 0.8px;
                                            right: 0.8px;
                                            bottom: 0.8px;
                                            border: 0;
                                            ${TypographyStyle} {
                                                color: ${color("common.black")};
                                            }
                                        }
                                    `,
                                },
                            } as any)}
                        </ElementWrapper>
                    </StyledWrapper>
                </GridItem>
                {/* <XNG-Changes: Input : Select Title ---------------------------------- */}
                <GridItem {...styles.smallGridItem}>
                    <SectionTitle>Input : Drop Down : Title</SectionTitle>
                    <StyledWrapper {...styles.columnFormWrapper}>
                        <ElementWrapper title="Inactive">
                            <Select label="Drop Down Title" value="" onChange={() => {}}>
                                <option value="">Select Option</option>
                                <option value="1">Unselected Option</option>
                                <option value="2">Unselected Option</option>
                                <option value="3">Unselected Option</option>
                                <option value="4">Unselected Option</option>
                            </Select>
                        </ElementWrapper>
                        <ElementWrapper title="Disabled">
                            <Select label="Drop Down Title" value="" onChange={() => {}} disabled>
                                <option value="">Select Option</option>
                                <option value="1">Unselected Option</option>
                                <option value="2">Unselected Option</option>
                                <option value="3">Unselected Option</option>
                                <option value="4">Unselected Option</option>
                            </Select>
                        </ElementWrapper>
                    </StyledWrapper>
                </GridItem>
                {/* <XNG-Changes: Input : Select Title ---------------------------------- */}

                {/* <XNG-Changes: Input : Select No Title ------------------------------- */}
                <GridItem {...styles.smallGridItem}>
                    <SectionTitle>Input : Drop Down : No Title</SectionTitle>
                    <StyledWrapper {...styles.columnFormWrapper}>
                        <ElementWrapper title="Inactive">
                            <Select value="" onChange={() => {}}>
                                <option value="">Select Option</option>
                                <option value="1">Unselected Option</option>
                                <option value="2">Unselected Option</option>
                                <option value="3">Unselected Option</option>
                                <option value="4">Unselected Option</option>
                            </Select>
                        </ElementWrapper>
                        <ElementWrapper title="Disabled">
                            <Select value="" onChange={() => {}} disabled>
                                <option value="">Select Option</option>
                                <option value="1">Unselected Option</option>
                                <option value="2">Unselected Option</option>
                                <option value="3">Unselected Option</option>
                                <option value="4">Unselected Option</option>
                            </Select>
                        </ElementWrapper>
                    </StyledWrapper>
                </GridItem>
                {/* <XNG-Changes: Input : Select No Title ------------------------------- */}

                {/* <XNG-Changes: Input : Dynamic Drop Down ----------------------------- */}
                <GridItem {...styles.smallGridItem}>
                    <SectionTitle>Input : Dynamic Drop Down</SectionTitle>
                    <StyledWrapper {...styles.columnFormWrapper}>
                        <ElementWrapper title="Inactive">
                            <DynamicDropdown
                                label="Drop Down Title"
                                options={dynamicDropdownOptions}
                                onSelectionChange={(value?: string) => {}}
                            />
                        </ElementWrapper>
                        <ElementWrapper title="Disabled">
                            <DynamicDropdown
                                label="Drop Down Title"
                                options={dynamicDropdownOptions}
                                onSelectionChange={(value?: string) => {}}
                                disabled
                            />
                        </ElementWrapper>
                    </StyledWrapper>
                </GridItem>
                {/* <XNG-Changes: Input : Dynamic Drop Down ----------------------------- */}

                {/* <XNG-Changes: Input : Dynamic Drop Down No Title -------------------- */}
                <GridItem {...styles.smallGridItem}>
                    <SectionTitle>Input : Drop Down : Small</SectionTitle>
                    <StyledWrapper {...styles.columnFormWrapper}>
                        <ElementWrapper title="Inactive">
                            <Select value="" onChange={() => {}} sizeVariant="small">
                                <option value="">Select Option</option>
                                <option value="1">Unselected Option</option>
                                <option value="2">Unselected Option</option>
                                <option value="3">Unselected Option</option>
                                <option value="4">Unselected Option</option>
                            </Select>
                        </ElementWrapper>
                        <ElementWrapper title="Disabled">
                            <Select value="" onChange={() => {}} disabled sizeVariant="small">
                                <option value="">Select Option</option>
                                <option value="1">Unselected Option</option>
                                <option value="2">Unselected Option</option>
                                <option value="3">Unselected Option</option>
                                <option value="4">Unselected Option</option>
                            </Select>
                        </ElementWrapper>
                    </StyledWrapper>
                </GridItem>
                {/* <XNG-Changes: Input : Dynamic Drop Down No Title -------------------- */}

                <GridItem {...styles.fullGridItem}>
                    <SectionContainer extendedStyles={{ gridContainer: { gap: 0 } }}>
                        <GridItem {...styles.smallGridItem}>
                            <SectionTitle>Input : Calendar</SectionTitle>
                            <StyledWrapper {...styles.columnFormWrapper}>
                                <ElementWrapper title="Inactive">
                                    <DatePicker label="Form Title" />
                                </ElementWrapper>
                                <ElementWrapper title="Disabled">
                                    <DatePicker label="Form Title" disabled />
                                </ElementWrapper>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.smallGridItem}>
                            <SectionTitle>Input : Text Area</SectionTitle>
                            <StyledWrapper {...styles.columnFormWrapper}>
                                <ElementWrapper title="Inactive">
                                    <TextArea label="Form Title" placeholder="Placeholder" />
                                </ElementWrapper>
                                <ElementWrapper title="Disabled">
                                    <TextArea label="Form Title" placeholder="Placeholder" disabled />
                                </ElementWrapper>
                            </StyledWrapper>
                        </GridItem>
                    </SectionContainer>
                </GridItem>

                <GridItem {...styles.smallGridItem}>
                    <SectionTitle>Input : Selector : Single</SectionTitle>
                    <StyledWrapper {...styles.columnFormWrapper}>
                        <ElementWrapper title="Inactive">
                            <QtyField
                                value={qty}
                                onChange={(e: any) => setQty(+e.target.value)}
                                showLabel={false}
                                extendedStyles={{
                                    editableQuantityTextField: {
                                        cssOverrides: {
                                            inputSelect: css`
                                                text-align: center;
                                                max-height: 32px;
                                                max-width: 34px;
                                            `,
                                        },
                                    },
                                }}
                            />
                        </ElementWrapper>
                        <ElementWrapper title="Disabled">
                            <QtyField
                                value={1}
                                disabled
                                showLabel={false}
                                extendedStyles={{
                                    editableQuantityTextField: {
                                        cssOverrides: {
                                            inputSelect: css`
                                                text-align: center;
                                                max-height: 32px;
                                                max-width: 34px;
                                            `,
                                        },
                                    },
                                }}
                            />
                        </ElementWrapper>
                    </StyledWrapper>
                </GridItem>

                <GridItem {...styles.smallGridItem}>
                    <SectionTitle>Selector : Toggles</SectionTitle>
                    <StyledWrapper {...styles.formWrapper}>
                        <CheckboxGroup
                            css={css`
                                min-width: 145px;
                            `}
                        >
                            <Checkbox variant="toggle" checked>
                                Right : Inactive
                            </Checkbox>
                            <Checkbox variant="toggle">Left : Inactive</Checkbox>
                            <Checkbox variant="toggle" checked disabled>
                                Right: Disabled
                            </Checkbox>
                        </CheckboxGroup>
                    </StyledWrapper>
                </GridItem>

                <GridItem {...styles.smallGridItem}>
                    <SectionTitle>Selector : Checkbox </SectionTitle>
                    <StyledWrapper {...styles.formWrapper}>
                        <CheckboxGroup label="Checkbox Heading">
                            <Checkbox>Unselected Checkbox</Checkbox>
                            <Checkbox checked>Selected Checkbox</Checkbox>
                            <Checkbox checked disabled>
                                Disabled Checked
                            </Checkbox>
                        </CheckboxGroup>
                    </StyledWrapper>
                </GridItem>

                <GridItem {...styles.smallGridItem}>
                    <SectionTitle>Selector : Radio Button</SectionTitle>
                    <StyledWrapper {...styles.columnFormWrapper}>
                        <RadioGroup
                            value={radioValue}
                            onChangeHandler={onRadioChangeHandler}
                            label="Radio Button Heading"
                        >
                            <Radio value="Unread" key="Unread">
                                Unselected Radio Button
                            </Radio>
                            <Radio value="All" key="All">
                                Selected Radio Button
                            </Radio>
                            <Radio key="Disabled" disabled={true}>
                                Disabled
                            </Radio>
                        </RadioGroup>
                    </StyledWrapper>
                </GridItem>

                {/* <XNG-Changes: Badging ------------------------------------------------ */}
                <GridItem {...styles.smallGridItem}>
                    <SectionTitle>Asset : Badging</SectionTitle>
                    <StyledWrapper {...styles.columnFormWrapper}>
                        <Badge
                            type={BadgeTypeValues.Text}
                            value="Blue Badge"
                            badgeTextColor={getSystemColor("common.ui1", 1, true).replace("#", "")}
                            badgeBackgroundColor={getSystemColor("primary", 1, true).replace("#", "")}
                            textBadgeCss={{
                                ...sharedBadgeStyle.badgeText,
                            }}
                            {...{
                                textBadgeWrapperCss: {
                                    ...mergeWithCss(sharedBadgeStyle.badge, {
                                        css: css`
                                            width: max-content;
                                            max-width: 100%;
                                        `,
                                    }),
                                },
                            }}
                        />
                        <Badge
                            type={BadgeTypeValues.Text}
                            value="Yellow Badge"
                            badgeTextColor={getSystemColor("common.ui7", 1, true).replace("#", "")}
                            badgeBackgroundColor={getSystemColor("secondary", 1, true).replace("#", "")}
                            textBadgeCss={{
                                ...sharedBadgeStyle.badgeText,
                            }}
                            {...{
                                textBadgeWrapperCss: {
                                    ...mergeWithCss(sharedBadgeStyle.badge, {
                                        css: css`
                                            width: 114px;
                                        `,
                                    }),
                                },
                            }}
                        />
                    </StyledWrapper>
                </GridItem>
            </SectionContainer>
        </>
    );
};

export default FormTab;

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const FormExample = () => {
    const formId = "myFormId";
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");

    const handleEmailChange = (e: any) => {
        setEmail(e.target.value);
    };

    const handleMessageChange = (e: any) => {
        setMessage(e.target.value);
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        const errors = xValidateOnSubmit(formId);
        // eslint-disable-next-line no-console
        console.log({ errors });
        if (errors.length > 0) {
            return;
        }
        // eslint-disable-next-line no-console
        console.log("Submitted");
    };

    return (
        <StyledForm onSubmit={handleSubmit}>
            <TextField
                label={translate("Email")}
                required={true}
                onChange={handleEmailChange}
                value={email}
                type="email"
                {...{ formId }}
            />

            <TextArea
                label={translate("Your Message")}
                onChange={handleMessageChange}
                required={true}
                value={message}
                {...{ formId }}
            />
            <Button type="submit">{translate("send")}</Button>
        </StyledForm>
    );
};
